import { useEffect } from 'react';
import useCoreStore from './coreStore';
import useProjectStore from './projectStore';

const cap = key => key.charAt(0).toUpperCase() + key.slice(1);

/**
 * Factory for useEnsure* hooks. All hooks have identical logic:
 * call ensureX() whenever projectId, cachedAt, or _invalidationSeq changes.
 *
 * `enabled` (default true) gates the fetch without breaking the rules of
 * hooks — callers that only need a slice once some condition is true (e.g.
 * a tab is actually open) pass `enabled={condition}` instead of calling the
 * hook conditionally. Once fetched, the existing cache TTL means flipping
 * enabled back to true later doesn't re-fetch unless the cache is stale.
 *
 * Adding a new slice: export one more line at the bottom of this file.
 */
function makeEnsureHook(key) {
  return function useEnsureSlice(enabled = true) {
    const projectId = useProjectStore(s => s.currentProjectId);
    const cachedAt  = useCoreStore(s => s[`${key}CachedAt`]);
    const seq       = useCoreStore(s => s._invalidationSeq);
    useEffect(() => {
      if (!projectId || !enabled) return;
      useCoreStore.getState()[`ensure${cap(key)}`]();
    }, [projectId, cachedAt, seq, enabled]);
  };
}

export const useEnsureHosts              = makeEnsureHook('hosts');
export const useEnsureApplications       = makeEnsureHook('applications');
export const useEnsureVulnerabilities    = makeEnsureHook('vulnerabilities');
export const useEnsureInstalledPackages  = makeEnsureHook('installedPackages');
export const useEnsureAssessmentsSummary = makeEnsureHook('assessmentsSummary');
export const useEnsureEnrichmentResults  = makeEnsureHook('enrichmentResults');
export const useEnsureArtifactRepositories = makeEnsureHook('artifactRepositories');
export const useEnsureExternalCmdbRecords  = makeEnsureHook('externalCmdbRecords');
export const useEnsureAppGraphData       = makeEnsureHook('appGraphData');
export const useEnsureHostsGraph         = makeEnsureHook('hostsGraph');
